<!-- src/views/appointments/AppointmentDetail.vue -->
<template>
  <div class="appointment-detail">
    <!-- Back button and actions bar -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <router-link to="/appointments" class="btn btn-outline-primary">
        <i class="bi bi-arrow-left me-2"></i>Volver
      </router-link>

      <div>
        <button v-if="appointment && appointment.estado !== 'completada'" class="btn btn-success me-2"
          @click="updateStatus('completada')">
          <i class="bi bi-check-circle me-1"></i> Marcar completada
        </button>
        <router-link :to="`/appointments/${appointmentId}/edit`" class="btn btn-primary">
          <i class="bi bi-pencil me-1"></i> Editar
        </router-link>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ error }}
      <div class="mt-3">
        <router-link to="/appointments" class="btn btn-outline-primary">
          <i class="bi bi-arrow-left me-2"></i>Volver a la lista de citas
        </router-link>
      </div>
    </div>

    <!-- Appointment details -->
    <div v-else-if="appointment" class="row">
      <div class="col-md-8">
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">Detalles de la Cita</h5>
          </div>
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-md-4 fw-bold">Estado:</div>
              <div class="col-md-8">
                <span :class="getStatusClass(appointment.estado)" class="badge rounded-pill">
                  {{ appointment.estado }}
                </span>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4 fw-bold">Fecha:</div>
              <div class="col-md-8">{{ formatDate(appointment.fecha) }}</div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4 fw-bold">Hora:</div>
              <div class="col-md-8">{{ formatTime(appointment.hora) }}</div>
            </div>
            <div class="row mb-3">
  <div class="col-md-4 fw-bold">Duración:</div>
  <div class="col-md-8">{{ appointment.duracion ? formatDuration(appointment.duracion) + ' minutos' : '30 minutos' }}</div>
</div>
            <div class="row mb-3">
              <div class="col-md-4 fw-bold">Tipo de Tratamiento:</div>
              <div class="col-md-8">{{ appointment.tipo_tratamiento }}</div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4 fw-bold">Notas:</div>
              <div class="col-md-8">
                <div v-if="appointment.notes" class="text-container">
                  <pre class="text-content">{{ appointment.notes }}</pre>
                </div>
                <p v-else class="text-muted">No hay notas disponibles</p>
              </div>
            </div>
          </div>
          <div class="card-footer bg-white">
            <div class="d-flex justify-content-between">
              <div>
                <button v-if="appointment.estado !== 'inasistencia'" class="btn btn-outline-danger"
                  @click="updateStatus('inasistencia')">
                  <i class="bi bi-x-circle me-1"></i> Cancelar cita
                </button>
              </div>
              <div>
                <button v-if="appointment.estado !== 'confirmada'" class="btn btn-outline-warning"
                  @click="showRescheduleModal = true">
                  <i class="bi bi-calendar-plus me-1"></i> Reprogramar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">Paciente</h5>
          </div>
          <div class="card-body">
            <div v-if="patient" class="d-flex align-items-center mb-3">
              <div class="avatar-placeholder me-3">
                {{ getInitials(patient.first_name, patient.last_name) }}
              </div>
              <div>
                <h5 class="mb-0">{{ patient.first_name }} {{ patient.last_name }}</h5>
                <p v-if="patient.fecha_nacimiento" class="text-muted mb-0">
                  {{ formatAge(patient.fecha_nacimiento) }} años
                </p>
              </div>
            </div>

            <div v-if="patient" class="patient-details">
              <div class="mb-2" v-if="patient.telefono">
                <i class="bi bi-telephone me-2"></i>
                {{ patient.telefono }}
              </div>
              <div v-if="patient.fecha_nacimiento">
                <i class="bi bi-calendar me-2"></i>
                {{ formatDate(patient.fecha_nacimiento) }}
              </div>
            </div>

            <div class="mt-3">
              <router-link :to="`/patients/${appointment.patient}`" class="btn btn-sm btn-outline-primary w-100">
                <i class="bi bi-person me-1"></i> Ver perfil del paciente
              </router-link>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Citas Anteriores</h5>
          </div>
          <div class="card-body p-0">
            <div v-if="previousAppointments.length === 0" class="text-center py-3">
              <p class="text-muted mb-0">No hay citas previas para este paciente</p>
            </div>
            <ul v-else class="list-group list-group-flush">
              <li v-for="prev in previousAppointments" :key="prev.id" class="list-group-item">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div>{{ formatDate(prev.fecha) }} - {{ formatTime(prev.hora) }}</div>
                    <small class="text-muted">{{ prev.tipo_tratamiento }}</small>
                  </div>
                  <span :class="getStatusClass(prev.estado)" class="badge rounded-pill">
                    {{ prev.estado }}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Reschedule Modal -->
    <div class="modal fade" :class="{ show: showRescheduleModal, 'd-block': showRescheduleModal }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Reprogramar Cita</h5>
            <button type="button" class="btn-close" @click="showRescheduleModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="rescheduleDate" class="form-label">Nueva Fecha</label>
              <input type="date" class="form-control" id="rescheduleDate" v-model="rescheduleForm.fecha">
            </div>
            <div class="mb-3">
              <label for="rescheduleTime" class="form-label">Nueva Hora</label>
              <input type="time" class="form-control" id="rescheduleTime" v-model="rescheduleForm.hora">
            </div>
            <div class="mb-3">
              <label for="rescheduleNotes" class="form-label">Razón del cambio</label>
              <textarea class="form-control" id="rescheduleNotes" rows="3" v-model="rescheduleForm.notes"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showRescheduleModal = false">Cancelar</button>
            <button type="button" class="btn btn-primary" @click="rescheduleAppointment" :disabled="updating">
              <span v-if="updating" class="spinner-border spinner-border-sm me-2" role="status"
                aria-hidden="true"></span>
              Guardar Cambios
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal backdrop -->
    <div v-if="showRescheduleModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import { appointmentService, patientService } from '@/services/api';
import { formatDateForDisplay, calculateAge, formatTime, formatDateForInput } from '@/utils/dateUtils';

export default {
  name: 'AppointmentDetail',
  data() {
    return {
      appointmentId: null,
      appointment: null,
      patient: null,
      previousAppointments: [],
      loading: true,
      error: null,
      updating: false,
      showRescheduleModal: false,
      rescheduleForm: {
        fecha: '',
        hora: '',
        notes: ''
      }
    };
  },
  created() {
    this.appointmentId = this.$route.params.id;
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;

        // Fetch appointment data
        const appointmentResponse = await appointmentService.get(this.appointmentId);
        this.appointment = appointmentResponse.data;

        // Fetch patient data
        if (this.appointment && this.appointment.patient) {
          const patientResponse = await patientService.get(this.appointment.patient);
          this.patient = patientResponse.data;

          // Fetch previous appointments for this patient
          await this.fetchPreviousAppointments();
        }
      } catch (error) {
        console.error('Error loading appointment details:', error);
        this.error = 'No se pudo cargar la información de la cita. Por favor, intenta de nuevo.';
      } finally {
        this.loading = false;
      }
    },
    
    async fetchPreviousAppointments() {
      try {
        // In a real app, you'd have an endpoint to get appointments by patient ID
        // Here we're simulating it by getting all and filtering
        const response = await appointmentService.getAll();

        // Get appointments for this patient, excluding the current one
        this.previousAppointments = response.data
          .filter(app =>
            app.patient === this.appointment.patient &&
            app.id !== parseInt(this.appointmentId)
          )
          .sort((a, b) => new Date(b.fecha) - new Date(a.fecha)) // Sort by date, newest first
          .slice(0, 5); // Limit to 5 previous appointments
      } catch (error) {
        console.error('Error fetching previous appointments:', error);
        // Don't set main error, just log it
      }
    },
    
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() : '';
      return `${first}${last}`;
    },
    
    formatDate(dateString) {
      return formatDateForDisplay(dateString);
    },
    
    formatDuration(minutes) {
      if (!minutes) return '30'; // Default to 30 minutes if not specified

      // Return duration as is if it's just minutes
      if (minutes < 60) {
        return minutes;
      }

      // Format as hours and minutes if it's 60 minutes or more
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;

      if (remainingMinutes === 0) {
        return `${hours} hora${hours > 1 ? 's' : ''}`;
      } else {
        return `${hours} hora${hours > 1 ? 's' : ''} y ${remainingMinutes} minutos`;
      }
    },
    
    formatTime(timeString) {
      return formatTime(timeString);
    },
    
    formatAge(dateString) {
      return calculateAge(dateString);
    },
    
    getStatusClass(status) {
      const statusClasses = {
        'programada': 'bg-info',
        'completada': 'bg-success',
        'inasistencia': 'bg-danger',
        'confirmada': 'bg-warning'
      };
      return statusClasses[status] || 'bg-secondary';
    },
    
    async updateStatus(newStatus) {
      if (!this.appointment) return;

      try {
        this.updating = true;

        // Update the appointment status
        await appointmentService.update(this.appointmentId, {
          ...this.appointment,
          estado: newStatus
        });

        // Refresh the appointment data
        await this.fetchData();
      } catch (error) {
        console.error('Error updating appointment status:', error);
        this.error = 'No se pudo actualizar el estado de la cita. Por favor, intenta de nuevo.';
      } finally {
        this.updating = false;
      }
    },
    
    async rescheduleAppointment() {
      if (!this.appointment) return;

      try {
        this.updating = true;

        // Create a new appointment with the rescheduled date and time
        const newAppointment = {
          patient: this.appointment.patient,
          fecha: this.rescheduleForm.fecha,
          hora: this.rescheduleForm.hora,
          tipo_tratamiento: this.appointment.tipo_tratamiento,
          estado: 'programada',
          notes: `Reprogramada desde ${this.formatDate(this.appointment.fecha)}. ${this.rescheduleForm.notes}`
        };

        await appointmentService.create(newAppointment);

        // Mark the current appointment as rescheduled
        await appointmentService.update(this.appointmentId, {
          ...this.appointment,
          estado: 'programada',
          notes: this.appointment.notes
            ? `${this.appointment.notes}\n\nReprogramada para ${this.formatDate(this.rescheduleForm.fecha)}.`
            : `Reprogramada para ${this.formatDate(this.rescheduleForm.fecha)}.`
        });

        // Close the modal and refresh data
        this.showRescheduleModal = false;
        await this.fetchData();
      } catch (error) {
        console.error('Error rescheduling appointment:', error);
        this.error = 'No se pudo reprogramar la cita. Por favor, intenta de nuevo.';
      } finally {
        this.updating = false;
      }
    }
  }
};
</script>

<style scoped>
.avatar-placeholder {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: bold;
  color: #495057;
}

.patient-details {
  margin-top: 1rem;
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.text-container {
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  padding: 0;
  overflow: hidden;
}

.text-content {
  font-family: inherit;
  margin: 0;
  padding: 0.75rem;
  background-color: transparent;
  border: none;
  font-size: 0.95rem;
  line-height: 1.5;
  white-space: pre-line;
  color: #212529;
  overflow-wrap: break-word;
  width: 100%;
}
</style>